import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTMLogContext } from "../../../context/domain/operations/TMLogContext";
import { Button, WorkerInitials } from "../../../components/ui";
import { Pencil, CurrencyDollar } from "../../../components/icons/svg/outline";
import {
  TicketLogTable,
  TicketLogRow,
  TicketLogFooter,
  TicketCard,
} from "../../../features/tickets";
import {
  TMStatusIndicator,
  getTMAmountDisplay,
  getTMAmountValue,
  getTMAmountTotals,
} from "../../../features/operations/tmlog";
import { formatDate, formatDateTime } from "../../../utils/datetime";
import { formatCurrency } from "../../../utils/common/formatting/numbers";
import {
  getTMLogById,
  createTMReport,
  getNextTMId,
  deleteTMReport,
} from "../../../services/api/tmlog";

const CLONE_EXCLUDE_KEYS = new Set([
  "id", "tm_id", "created_at", "updated_at", "created_by",
  "submitted", "submitted_at", "locked", "update_history",
  "job_number", "job_description", "pricing", "inspectors",
  "estimated_cost",
]);

/**
 * T&M Log List Page
 * Displays all T&M reports in table, card, or group-by view.
 *
 * Uses TicketLogTable from generic ticket system (same as RFI).
 * Clone uses getNextTMId then create (backend expects tm_id on create).
 */
const TMLog = () => {
  const navigate = useNavigate();
  const { reports, loading, highlightAddedItemId, highlightDeletedItemId, reportNewItem, executeDelete } = useTMLogContext();
  const [filteredCount, setFilteredCount] = useState(0);
  const [filteredTotals, setFilteredTotals] = useState({
    estimated: 0,
    priced: 0,
  });
  const [paginationInfo, setPaginationInfo] = useState(null);

  const handleClone = async (report) => {
    try {
      const original = await getTMLogById(report.id);
      if (!original) return;

      const payload = {};
      Object.entries(original).forEach(([key, value]) => {
        if (CLONE_EXCLUDE_KEYS.has(key)) return;
        if (value && typeof value === "object" && !Array.isArray(value) && "id" in value) {
          payload[key] = value.id;
        } else {
          payload[key] = value;
        }
      });

      const jobNumber = original.job_number ?? original.job?.number;
      if (jobNumber) {
        try {
          const res = await getNextTMId(jobNumber);
          if (res?.tm_id) payload.tm_id = res.tm_id;
        } catch (e) {
          console.warn("Could not get next TM ID for clone:", e);
        }
      }

      const newReport = await createTMReport(payload);
      await reportNewItem(newReport?.id);
    } catch (error) {
      console.error("T&M clone failed:", error);
    }
  };

  useEffect(() => {
    document.title = "T&M Log - R&S";
  }, []);

  const handleCreateNew = () => {
    navigate("/operations/tm-log/report");
  };

  const handleEdit = (report) => {
    navigate(`/operations/tm-log/report/${report.id}`);
  };

  const handlePrice = (report) => {
    navigate(`/operations/tm-log/price/${report.id}`);
  };

  // List columns for TicketLogTable (status uses TMStatusIndicator)
  const columns = [
    {
      key: "tm_id",
      label: "T&M ID",
      width: "11%",
      minWidth: "100px",
      sortable: true,
      cellClassName: "px-2 pl-4 py-4 font-mono text-[15px] text-green-700 whitespace-nowrap",
      render: (item) => item.tm_id || "N/A",
    },
    {
      key: "status",
      label: "Status",
      width: "10%",
      sortable: false,
      hideOnMobile: true,
      cellClassName: "px-2 py-4 whitespace-nowrap",
      render: (item) => <TMStatusIndicator log={item} />,
    },
    {
      key: "job_number",
      label: "Project",
      maxWidth: "130px",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "px-2 py-4 max-w-[130px]",
      render: (item) => {
        const p =
          item.job_number && item.job_description
            ? `${item.job_number} - ${item.job_description}`
            : item.job_number
              ? String(item.job_number)
              : "N/A";
        return <div className="truncate">{p}</div>;
      },
    },
    {
      key: "date_performed",
      label: "Date Performed",
      width: "11%",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "px-2 py-4 whitespace-nowrap",
      render: (item) =>
        item.date_performed
          ? formatDate(item.date_performed, "d mon DD, YYYY")
          : "N/A",
    },
    {
      key: "updated_at",
      label: "Last Edited",
      width: "11%",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "px-2 py-4 whitespace-nowrap text-gray-500",
      render: (item) =>
        item.updated_at
          ? formatDateTime(item.updated_at, "mon DD, YYYY")
          : item.created_at
            ? formatDateTime(item.created_at, "mon DD, YYYY")
            : "N/A",
    },
    {
      key: "created_by",
      label: "By",
      width: "5%",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "px-2 py-4 max-w-[20px]",
      getSortValue: (item) => item.created_by?.name ?? "",
      render: (item) =>
        item.created_by ? (
          <WorkerInitials
            worker={item.created_by}
            tooltip
          />
        ) : (
          "-"
        ),
    },
    {
      key: "description",
      label: "Work Description",
      width: "18%",
      maxWidth: "320px",
      sortable: true,
      cellClassName: "px-2 py-4 max-w-md",
      render: (item) => (
        <div className="text-xs line-clamp-2">
          {item.description || "No description"}
        </div>
      ),
    },
    {
      key: "amount",
      label: "Amount",
      width: "10%",
      sortable: true,
      hideOnMobile: true,
      cellClassName: "px-2 py-4 whitespace-nowrap",
      getSortValue: (item) => getTMAmountValue(item),
      render: (item) => {
        const amount = getTMAmountDisplay(item);
        if (amount.value == null) {
          return (
            <span className={amount.textClassName} title={amount.tooltip || undefined}>
              -
            </span>
          );
        }
        return (
          <span className={amount.textClassName} title={amount.tooltip}>
            {formatCurrency(amount.value)}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      width: "50px",
      cellClassName: "px-2 md:p-4 py-3 whitespace-nowrap",
      hideOnMobile: false,
      isActions: true,
    },
  ];

  const statusFilters = [
    { value: "all", label: "All Statuses" },
    { value: "priced", label: "Priced" },
    { value: "submitted", label: "Submitted" },
  ];

  const statusFilterFn = (item, status) => {
    if (status === "priced") return !!item.pricing;
    if (status === "submitted") return !!item.submitted;
    return true;
  };

  if (loading) {
    return <div className="p-4">Loading...</div>;
  }

  return (
    <div className="p-2 md:p-4 max-w-[1420px] md:min-w-[1240px] mx-auto">
      <div className="bg-green-900 text-white p-4 rounded-t-lg flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">T&M Log</h1>
          <p className="text-sm pr-2">Time & Materials Documentation Log</p>
        </div>
        <div>
          <Button
            variant="custom"
            text="Create"
            icon="plus"
            onClick={handleCreateNew}
            className="md:hidden px-4 py-2 bg-green-600 text-white hover:bg-green-700"
          />
          <Button
            variant="custom"
            text="Create New T&M Tag"
            icon="plus"
            onClick={handleCreateNew}
            className="hidden md:inline-flex px-4 py-2 bg-green-600 text-white hover:bg-green-700"
          />
        </div>
      </div>

      <div className="bg-white rounded-b-lg shadow">
        <TicketLogTable
          data={reports}
          columns={columns}
          RowComponent={TicketLogRow}
          CardComponent={TicketCard}
          onRowClick={handleEdit}
          getRowHref={(item) => `/operations/tm-log/report/${item.id}`}
          searchFields={["tm_id", "description", "job_number"]}
          searchPlaceholder="Search by T&M ID, Job ID, or description..."
          statusFilters={statusFilters}
          statusFilterFn={statusFilterFn}
          onFilteredCountChange={setFilteredCount}
          onFilteredTotalChange={setFilteredTotals}
          onPaginationChange={setPaginationInfo}
          calculateTotal={getTMAmountTotals}
          emptyMessage="No T&M reports found"
          defaultSort={{ field: "updated_at", direction: "desc" }}
          defaultView="group"
          groupBy="job_number"
          highlightAddedItemId={highlightAddedItemId}
          highlightDeletedItemId={highlightDeletedItemId}
          onDelete={(item) => deleteTMReport(item.id)}
          executeDelete={executeDelete}
          getGroupLabel={(groupValue, items) => {
            if (!groupValue) return "No Project";
            const first = items[0];
            const label =
              first?.job_number && first?.job_description
                ? `${first.job_number} - ${first.job_description}`.trim()
                : first?.job_number
                  ? `Project ${first.job_number}`
                  : "Unknown Project";
            return label;
          }}
          rowProps={{
            onClone: handleClone,
            customActions: (item) => [
              <Button
                key="edit"
                variant="custom"
                text="Edit"
                icon={<Pencil className="w-4 h-4 text-green-700 translate-x-[2px]" />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(item);
                }}
                className="text-sm border-[1px] text-green-700 border-green-300 bg-green-50 hover:bg-green-200 ease-in-out px-1 pr-2 py-1"
              />,
              <Button
                key="price"
                variant="custom"
                text="Price"
                icon={<CurrencyDollar className="w-4 h-4 text-orange-600 translate-x-[2px]" />}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrice(item);
                }}
                className="hidden md:inline-flex text-sm border-[1px] text-orange-700 border-orange-300 bg-orange-50 hover:bg-orange-100 ease-in-out px-1 pr-2 py-1"
              />,
            ],
          }}
          cardProps={{
            onClone: handleClone,
            customActions: (item) => [
              <Button
                key="edit"
                variant="custom"
                text="Edit"
                icon={<Pencil className="w-4 h-4 text-green-700 translate-x-[2px]" />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(item);
                }}
                className="text-sm border-[1px] text-green-700 border-green-300 bg-green-50 hover:bg-green-200 px-1 pr-2 py-1"
              />,
              <Button
                key="price"
                variant="custom"
                text="Price"
                icon={<CurrencyDollar className="w-4 h-4 text-orange-600 translate-x-[2px]" />}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrice(item);
                }}
                className="text-sm border-[1px] text-orange-700 border-orange-300 bg-orange-50 hover:bg-orange-100 px-1 pr-2 py-1"
              />,
            ],
          }}
        />
        <TicketLogFooter
          paginationInfo={paginationInfo}
          filteredCount={filteredCount}
          totalCount={reports.length}
          itemLabel="T&M tag"
          accentClassName="text-green-800"
          selectFocusClassName="focus:border-green-500 focus:ring-green-500"
          endContent={(
            <p className="font-semibold text-black">
              Total Value:{" "}
              <span className="text-gray-500" title="Estimated">
                {formatCurrency(filteredTotals.estimated)}
              </span>
              /
              <span className="text-green-700" title="Priced">
                {formatCurrency(filteredTotals.priced)}
              </span>
            </p>
          )}
        />
      </div>
    </div>
  );
};

export default TMLog;
